import {
  APIFetch,
  APIFetchV3,
  RequestHeaders,
  serverlessAPIFetch,
  tenant
} from "@certa/network";

import {
  createDataObjectDetailsModel,
  createDataObjectModel,
  getDataObjectTypesModel,
  createDataObjectWorkflowRelationActionsModel,
  createDataObjectActivityLogsModel,
  createDataObjectRelatedObjectsModel,
  createDataObjectStatusesModel,
  createDataObjectTimeSeriesModel,
  createCopySourceObjectsModel,
  createCopyStepModel,
  createDataObjectAccessibleSectionsModel,
  createDataObjectTypesModelWithTranslations,
  createDataObjectFileUrlsModel,
  createDataObjectListModel,
  createSurveyTableFieldDataModel,
  createSelectionTableFieldDataModel,
  createSelectionResponseModel,
  createDataObjectAccessibleSchemaModel
} from "../models/dataObject.model";

import type {
  CreateDataObjectModelResponse,
  CreateDataObjectDetailsModelResponse,
  GetDataObjectDetailsRequestParams,
  GetDataObjectTypesRaw,
  CreateDataObjectWorkflowRelationActionsModelResponse,
  DataObjectWorkflowRelation,
  CreateDataObjectActivityLogsModelResponse,
  CreateDataObjectRelatedObjectsModelResponse,
  CreateDataObjectStatusesModelResponse,
  CreateDataObjectTimeSeriesModelResponse,
  CreateCopySourceObjectsModelResponse,
  CreateCopyStepModelResponse,
  CreateDataObjectAccessibleSectionsModelResponse,
  CreateDataObjectTypesModelWithTranslationsResponse,
  CreateDataObjectFileUrlsModelResponse,
  GetDataObjectListResponse,
  GetDataObjectListModalReturn,
  CreateDataObjectAccessibleSchemaModelResponse,
  CreateDataObjectAccessibleSchemaModelReturn,
  RelatedObjectsFilters,
  GetSelectionTableFieldDataResponse,
  GetSelectionTableFieldDataReturn,
  GetSelectionResponseReturn,
  GetSelectionTableFieldDataParams
} from "../types/dataObjectsAPIs.types";
import type {
  GetSurveyTableFieldDataParams,
  GetSurveyTableFieldDataReturn
} from "../types/dataObjectsSurveyTable.types";
import { activityLogModelCreator } from "../models/activityLog.model";
import type { ActivityLog } from "../types/activityLog.types";

export const getDataObjectTypes = () => {
  const url = `objects/object-types/`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<GetDataObjectTypesRaw>(url, requestOptions).then(response =>
    getDataObjectTypesModel(response)
  );
};

type GetObjectTypeTranslationsRequest = {
  data: {
    translated_ui_schema: null;
    tag: null;
    id: null;
    schema: null;
    ui_schema: null;
    filter_config: null;
  };
  filters?: {
    tag__in?: string;
  };
};

export const getObjectTypeTranslations = (
  request: GetObjectTypeTranslationsRequest
) => {
  const url = `objects/object-types-list-drest/?limit=none`;

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(request)
  };

  return APIFetch<CreateDataObjectTypesModelWithTranslationsResponse>(
    url,
    requestOptions
  ).then(response => createDataObjectTypesModelWithTranslations(response));
};

/**
 * Fetches the standard data objects for a workflow.
 * @param workflowId
 * @returns
 */
export const getDataObjectsForWorkflow = (workflowId: number) => {
  const url = `objects/${workflowId}/`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<CreateDataObjectModelResponse>(url, requestOptions).then(
    response => createDataObjectModel(response)
  );
};

/**
 * Fetches the data objects dynamically based on filters provided.
 * @param params
 * @returns
 */
export const getDataObjectDetails = (
  params: GetDataObjectDetailsRequestParams,
  offset: number = 0
) => {
  const url = `objects/object-instances-list-drest/?limit=20&offset=${offset}`;

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(params)
  };

  return APIFetch<CreateDataObjectDetailsModelResponse>(
    url,
    requestOptions
  ).then(createDataObjectDetailsModel);
};

type GetDataObjectWorkflowRelationActionsParams = {
  objectTypeTag?: string;
  workflowKindTag?: string;
  relation?: DataObjectWorkflowRelation;
  objectUid?: string;
};

export const getDataObjectWorkflowRelationActions = (
  params: GetDataObjectWorkflowRelationActionsParams
) => {
  const {
    objectTypeTag,
    workflowKindTag,
    objectUid,
    relation = "initializer_object"
  } = params;

  const queryParams = new URLSearchParams();

  if (relation) queryParams.append("relation__in", relation);
  if (objectTypeTag) queryParams.append("object_type_tag", objectTypeTag);
  if (workflowKindTag) queryParams.append("workflow_kind_tag", workflowKindTag);
  if (objectUid) queryParams.append("object_uid", objectUid);

  const url = `objects/object-type-workflow-kind-relations/${queryParams.toString() ? "?" + queryParams.toString() : ""}`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  // We are not using pagination here because we are fetching all the
  // workflow kind relations for a given object type.
  // The workflow kinds will not be more than 20 in most cases and this is configurable.
  // TODO: Will have to revisit this if we ever have more than 20 workflow kinds.
  return APIFetch<CreateDataObjectWorkflowRelationActionsModelResponse>(
    url,
    requestOptions
  ).then(createDataObjectWorkflowRelationActionsModel);
};

export const getDataObjectActivityLogs = (objectId: string) => {
  const url = `objects/activity-logs/?object_uid=${objectId}`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<CreateDataObjectActivityLogsModelResponse>(
    url,
    requestOptions
  ).then(createDataObjectActivityLogsModel);
};

type GetRelatedObjectsParams = {
  objectReference: string;
  workflowId: number;
  relationPath?: string;
  filters?: RelatedObjectsFilters;
};

export const getRelatedObjects = (params: GetRelatedObjectsParams) => {
  const { objectReference, workflowId, relationPath, filters } = params;

  const url = new URLSearchParams();
  url.set("object_reference", objectReference);
  url.set("workflow_id", workflowId.toString());
  if (relationPath) {
    url.set("relation_path", relationPath);
  }

  // Add filter parameters to the URL
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.set(key, String(value));
      }
    });
  }

  const endpoint = `objects/related-objects/?${url.toString()}`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<CreateDataObjectRelatedObjectsModelResponse>(
    endpoint,
    requestOptions
  ).then(createDataObjectRelatedObjectsModel);
};

export const getObjectsStatuses = () => {
  const url = `objects/statuses?limit=none`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<CreateDataObjectStatusesModelResponse>(
    url,
    requestOptions
  ).then(createDataObjectStatusesModel);
};

type GetDataObjectTimeSeriesParams = {
  objectId: string;
  identifier_field: string;
  data_points: string[];
};

export const getDataObjectTimeSeries = (
  params: GetDataObjectTimeSeriesParams
) => {
  const { objectId, ...restParams } = params;

  const url = `data-monitoring/projection/${objectId}/`;

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(restParams)
  };

  return APIFetchV3<CreateDataObjectTimeSeriesModelResponse>(
    url,
    requestOptions
  ).then(createDataObjectTimeSeriesModel);
};

type GetCopySourceObjectsParams = {
  stepId: number;
  offset?: number;
};

export const getCopySourceObjects = (params: GetCopySourceObjectsParams) => {
  const { stepId, offset = 0 } = params;

  const url = `steps/${stepId}/copy-source-objects/?limit=20&offset=${offset}`;

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchV3<CreateCopySourceObjectsModelResponse>(
    url,
    requestOptions
  ).then(createCopySourceObjectsModel);
};

type GetCopyStepParams = {
  stepId: number;
  sourceObjectId: string;
};

export const getCopyStep = (params: GetCopyStepParams) => {
  const { stepId, sourceObjectId } = params;

  const url = `steps/${stepId}/copy/`;

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({ source_object_id: sourceObjectId })
  };

  return APIFetchV3<CreateCopyStepModelResponse>(url, requestOptions).then(
    createCopyStepModel
  );
};

type ResetCopyStepParams = {
  stepId: number;
};

export const resetCopyStep = (params: ResetCopyStepParams) => {
  const { stepId } = params;

  const url = `steps/${stepId}/copy/`;

  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.DELETE,
    credentials: "include"
  };

  return APIFetchV3<CreateCopyStepModelResponse>(url, requestOptions).then(
    createCopyStepModel
  );
};

export const getObjectScreeningActivityLogs = (objectId: string) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-tenant": tenant
    },
    credentials: "include"
  };

  const url = `object-instances/activity/?object_ids=${objectId}&actions=screening_adjudication_updated`;

  return serverlessAPIFetch<ActivityLog>(url, requestOptions).then(
    activityLogModelCreator
  );
};

export const getDataObjectAccessibleSections = (objectId: string) => {
  const url = `objects/accessible-sections/${objectId}/`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<CreateDataObjectAccessibleSectionsModelResponse>(
    url,
    requestOptions
  ).then(createDataObjectAccessibleSectionsModel);
};

export const getDataObjectFileUrls = (objectId: string) => {
  const url = `objects/get-file-urls/${objectId}/`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<CreateDataObjectFileUrlsModelResponse>(
    url,
    requestOptions
  ).then(createDataObjectFileUrlsModel);
};

export type GetDataObjectListParams = {
  objectTag: string;
  search?: string;
  name?: string;
  status__in?: string;
  ordering?: string;
  offset?: number;
  limit?: number;
  relation_name?: string;
  related_object_uid?: string;
  object_type_tag?: string;
  created_at__date_range_before?: string;
  created_at__date_range_after?: string;
  updated_at__date_range_before?: string;
  updated_at__date_range_after?: string;
  uid__in?: string;
};

export const getDataObjectList = async (
  params: GetDataObjectListParams
): Promise<GetDataObjectListModalReturn> => {
  const { objectTag, ...queryParams } = params;
  const searchParams = new URLSearchParams();

  // Add all query params to searchParams
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, value.toString());
    }
  });

  const queryString = searchParams.toString();
  const url = `objects/objects-data/${objectTag}/${queryString ? `?${queryString}` : ""}`;
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<GetDataObjectListResponse>(url, requestOptions).then(
    createDataObjectListModel
  );
};

export const getObjectAccessibleSchema = (
  objectTag: string
): Promise<CreateDataObjectAccessibleSchemaModelReturn> => {
  const url = `objects/accessible-schema/${objectTag}/`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetch<CreateDataObjectAccessibleSchemaModelResponse>(
    url,
    requestOptions
  ).then(createDataObjectAccessibleSchemaModel);
};

export const exportObjectReport = (reportId: number, filters?: any) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: filters ? JSON.stringify({ filters }) : JSON.stringify({})
  };

  const exportUrl = `objects/reports/${reportId}/export/`;
  return APIFetch(exportUrl, requestOptions);
};

export const getSurveyTableFieldData = async (
  params: GetSurveyTableFieldDataParams
): Promise<GetSurveyTableFieldDataReturn> => {
  const { fieldId, ...queryParams } = params;
  const searchParams = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const queryString = searchParams.toString();
  const url = `data-object-survey-table-field-options/${fieldId}/${queryString ? `?${queryString}` : ""}`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV3<GetDataObjectListResponse>(url, requestOptions).then(
    createSurveyTableFieldDataModel
  );
};

export type ExportSurveyTableFieldDataParams = {
  fieldId: string;
};

export const exportSurveyTableFieldData = async (
  params: ExportSurveyTableFieldDataParams
): Promise<Response> => {
  const { fieldId } = params;

  const url = `data-object-survey-table-field-options/${fieldId}/export`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV3(url, requestOptions);
};

export const getSelectionTableFieldData = async (
  params: GetSelectionTableFieldDataParams
): Promise<GetSelectionTableFieldDataReturn> => {
  const { fieldId, ...queryParams } = params;
  const searchParams = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const queryString = searchParams.toString();
  const url = `data-objects-selection-table-field-options/${fieldId}/${queryString ? `?${queryString}` : ""}`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV3<GetSelectionTableFieldDataResponse>(
    url,
    requestOptions
  ).then(createSelectionTableFieldDataModel);
};

export type GetSelectionResponseParams = {
  workflowId: number;
  fieldTag: string;
};

export type GetSelectionResponseData = {
  id: number;
  object_ids: number[];
};

export const getSelectionResponse = async (
  params: GetSelectionResponseParams
): Promise<GetSelectionResponseReturn> => {
  const { workflowId, fieldTag } = params;
  const searchParams = new URLSearchParams();
  searchParams.append("workflow_id", workflowId.toString());
  searchParams.append("field_tag", fieldTag);

  const url = `data-objects-selection-response/?${searchParams.toString()}`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV3<GetSelectionResponseData>(url, requestOptions).then(
    createSelectionResponseModel
  );
};

export type SelectObjectParams = {
  workflowId: number;
  fieldTag: string;
  objectIds: number[];
};

export const selectObject = async (
  params: SelectObjectParams
): Promise<void> => {
  const { workflowId, fieldTag, objectIds } = params;

  const url = `data-objects-selection-response/`;

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      workflow_id: workflowId,
      field_tag: fieldTag,
      object_ids: objectIds
    })
  };

  return APIFetchV3(url, requestOptions);
};

export type DeselectObjectParams = {
  workflowId: number;
  fieldTag: string;
  objectIds: number[];
};

export const deselectObject = async (
  params: DeselectObjectParams
): Promise<void> => {
  const { workflowId, fieldTag, objectIds } = params;

  const url = `data-objects-selection-response/`;

  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.DELETE,
    credentials: "include",
    body: JSON.stringify({
      workflow_id: workflowId,
      field_tag: fieldTag,
      object_ids: objectIds
    })
  };

  return APIFetchV3(url, requestOptions);
};
